const ORDER_STATUS_LABELS_EN = {
  Created: "Created",
  Pending: "Pending",
  Confirmed: "Confirmed",
  Processing: "Processing",
  Shipping: "Shipped",
  Shipped: "Shipped",
  Completed: "Delivered",
  Delivered: "Delivered",
  Cancelled: "Cancelled",
  Returned: "Returned",
  Expired: "Expired",
  RequiresReview: "Requires Review",
};

const ORDER_STATUS_LABELS_AR = {
  Created: "تم الإنشاء",
  Pending: "قيد الانتظار",
  Confirmed: "تم التأكيد",
  Processing: "قيد التجهيز",
  Shipping: "جاري الشحن",
  Shipped: "جاري الشحن",
  Completed: "تم التوصيل",
  Delivered: "تم التوصيل",
  Cancelled: "ملغي",
  Returned: "مرتجع",
  Expired: "منتهي الصلاحية",
  RequiresReview: "يتطلب مراجعة",
};

/**
 * Normalizes any order status representation (numeric enum 1-9, english name, or arabic text)
 * to a canonical PascalCase string ("Pending", "Confirmed", "Processing", "Shipping", "Completed", "Cancelled", "Returned", "Expired", "RequiresReview").
 */
export function getCanonicalOrderStatus(status) {
  if (status === null || status === undefined) return "";
  const s = String(status).trim().toLowerCase();

  if (status === 1 || s === "1" || s === "pending" || s === "قيد الانتظار") return "Pending";
  if (status === 2 || s === "2" || s === "confirmed" || s === "تم التأكيد") return "Confirmed";
  if (status === 3 || s === "3" || s === "processing" || s === "قيد التجهيز") return "Processing";
  if (status === 4 || s === "4" || s === "shipping" || s === "shipped" || s === "جاري الشحن") return "Shipping";
  if (status === 5 || s === "5" || s === "completed" || s === "delivered" || s === "تم التوصيل") return "Completed";
  if (status === 6 || s === "6" || s === "cancelled" || s === "canceled" || s === "ملغي" || s === "ملغى") return "Cancelled";
  if (status === 7 || s === "7" || s === "returned" || s === "مرتجع") return "Returned";
  if (status === 8 || s === "8" || s === "expired" || s === "منتهي" || s === "منتهي الصلاحية") return "Expired";
  if (status === 9 || s === "9" || s === "requiresreview" || s === "requires_review" || s === "يتطلب مراجعة") return "RequiresReview";
  if (s === "created" || s === "تم الإنشاء" || s === "0") return "Created";

  return String(status);
}

export function isOrderShippable(status) {
  const canonical = getCanonicalOrderStatus(status);
  return ["Created", "Pending", "Confirmed", "Processing"].includes(canonical);
}

export function isOrderDeliverable(status) {
  const canonical = getCanonicalOrderStatus(status);
  return canonical === "Shipping";
}

export function isOrderCancellable(status) {
  const canonical = getCanonicalOrderStatus(status);
  return ["Created", "Pending", "Confirmed", "Processing"].includes(canonical);
}

export function isOrderReturnable(status) {
  const canonical = getCanonicalOrderStatus(status);
  return canonical === "Completed";
}

export function getOrderStatusBadgeClass(status) {
  const canonical = getCanonicalOrderStatus(status);
  if (canonical === "Completed") return "admin-badge--delivered";
  if (canonical === "Shipping") return "admin-badge--shipped";
  if (canonical === "Cancelled" || canonical === "Returned" || canonical === "Expired") return "admin-badge--bad";
  if (canonical === "Confirmed" || canonical === "Processing") return "admin-badge--good";
  return "admin-badge--neutral";
}

function getCurrentLanguage() {
  try {
    return localStorage.getItem("tl_storefront_lang") || "ar";
  } catch {
    return "ar";
  }
}

export function formatMoney(value, lang) {
  const n = typeof value === "string" ? parseFloat(value, 10) : Number(value);
  if (Number.isNaN(n)) return "—";
  const effectiveLang = lang || getCurrentLanguage();

  if (effectiveLang === "ar") {
    const formatted = n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${formatted} ج.م`;
  }

  return new Intl.NumberFormat("en-US", { style: "currency", currency: "EGP" }).format(n);
}

export function formatDate(iso, lang) {
  if (!iso) return "—";
  const effectiveLang = lang || getCurrentLanguage();
  try {
    return new Date(iso).toLocaleDateString(effectiveLang === "ar" ? "ar-EG" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function formatOrderStatus(status, lang) {
  if (status === null || status === undefined) return "—";
  const canonical = getCanonicalOrderStatus(status);
  const effectiveLang = lang || getCurrentLanguage();
  const dict = effectiveLang === "ar" ? ORDER_STATUS_LABELS_AR : ORDER_STATUS_LABELS_EN;

  return dict[canonical] || dict[status] || String(status);
}

const EN_SIZE_MAP = {
  Years6: "6 Years",
  Years8: "8 Years",
  Years10: "10 Years",
  Years12: "12 Years",
  Years14: "14 Years",
  YouthXS: "Youth XS",
  YouthS: "Youth S",
  YouthM: "Youth M",
  YouthL: "Youth L",
  YouthXL: "Youth XL",
};

const AR_SIZE_MAP = {
  Preemie: "مبتسرين",
  Newborn: "حديث الولادة",
  Months0To3: "0 - 3 أشهر",
  Months3To6: "3 - 6 أشهر",
  Months6To9: "6 - 9 أشهر",
  Months9To12: "9 - 12 شهر",
  Months12To18: "12 - 18 شهر",
  Months18To24: "18 - 24 شهر",
  T2: "سنتين (T2)",
  T3: "3 سنوات (T3)",
  T4: "4 سنوات (T4)",
  T5: "5 سنوات (T5)",
  Years6: "6 سنوات",
  Years8: "8 سنوات",
  Years10: "10 سنوات",
  Years12: "12 سنة",
  Years14: "14 سنة",
  YouthXS: "أطفال XS",
  YouthS: "أطفال S",
  YouthM: "أطفال M",
  YouthL: "أطفال L",
  YouthXL: "أطفال XL",
  XXS: "XXS",
  XS: "XS",
  S: "S",
  M: "M",
  L: "L",
  XL: "XL",
  XXL: "2XL",
  X3L: "3XL",
  X4L: "4XL",
  X5L: "5XL",
  X6L: "6XL",
  X7L: "7XL",
  LT: "Large Tall",
  XLT: "XL Tall",
  XXLT: "2XL Tall",
};

/** Pretty-print ProductSize enum strings from API */
export function formatProductSize(size, lang) {
  if (!size || size === "None") return "";
  const effectiveLang = lang || getCurrentLanguage();

  if (effectiveLang === "ar") {
    if (AR_SIZE_MAP[size]) {
      return AR_SIZE_MAP[size];
    }
    const yearsMatch = String(size).match(/^Years(\d+)$/i);
    if (yearsMatch) {
      const num = parseInt(yearsMatch[1], 10);
      return num <= 10 ? `${num} سنوات` : `${num} سنة`;
    }
  }

  if (EN_SIZE_MAP[size]) {
    return EN_SIZE_MAP[size];
  }

  const yearsMatch = String(size).match(/^Years(\d+)$/i);
  if (yearsMatch) {
    return `${yearsMatch[1]} Years`;
  }

  return size
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^Months/, "Months ")
    .trim();
}

export function formatGovernorate(name) {
  if (!name || name === "None") return "";
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^Kafr/, "Kafr ")
    .trim();
}

const SHIPMENT_STATUS_LABELS_EN = {
  Created: "Shipment Created",
  RouteAssigned: "Assigned to Courier",
  PickedUp: "Picked Up by Courier",
  PickingUpFromConsignee: "Courier En Route to Pickup",
  InTransit: "In Transit",
  Delivered: "Delivered",
  ReturnedToBusiness: "Returned to Warehouse",
  Exception: "Delivery Issue / Attempted",
  Terminated: "Terminated",
  Cancelled: "Cancelled",
  Lost: "Lost in Transit",
  Damaged: "Damaged",
  Investigation: "Under Investigation",
  AwaitingAction: "Awaiting Action",
  OnHold: "On Hold",
};

const SHIPMENT_STATUS_LABELS_AR = {
  Created: "تم إنشاء الشحنة",
  RouteAssigned: "تم التعيين للمندوب",
  PickedUp: "تم الاستلام من المخزن",
  PickingUpFromConsignee: "المندوب في الطريق للاستلام",
  InTransit: "في الطريق إليك",
  Delivered: "تم التوصيل بنجاح",
  ReturnedToBusiness: "تم الإرجاع للمخزن",
  Exception: "تعذر التوصيل / قيد المتابعة",
  Terminated: "منتهية",
  Cancelled: "ملغاة",
  Lost: "مفقودة في الشحن",
  Damaged: "تالفة",
  Investigation: "قيد التحقيق",
  AwaitingAction: "بانتظار إجراء",
  OnHold: "معلقة مؤقتاً",
};

const SHIPMENT_STATUS_NUMBER_MAP = {
  1: "Created",
  2: "RouteAssigned",
  3: "PickedUp",
  4: "PickingUpFromConsignee",
  5: "InTransit",
  6: "Delivered",
  7: "ReturnedToBusiness",
  8: "Exception",
  9: "Terminated",
  10: "Cancelled",
  11: "Lost",
  12: "Damaged",
  13: "Investigation",
  14: "AwaitingAction",
  15: "OnHold",
};

export function formatShipmentStatus(status, lang) {
  if (status === null || status === undefined) return "—";
  const effectiveLang = lang || getCurrentLanguage();
  const dict = effectiveLang === "ar" ? SHIPMENT_STATUS_LABELS_AR : SHIPMENT_STATUS_LABELS_EN;

  let key = status;
  if (typeof status === "number" && SHIPMENT_STATUS_NUMBER_MAP[status]) {
    key = SHIPMENT_STATUS_NUMBER_MAP[status];
  }
  return dict[key] || String(key);
}

const DELIVERY_TYPE_LABELS_EN = {
  Forward: "Order Delivery",
  Return: "Return Pickup",
  Exchange: "Exchange Delivery",
  CashCollection: "Cash Collection",
};

const DELIVERY_TYPE_LABELS_AR = {
  Forward: "شحنة توصيل الطلب",
  Return: "شحنة إرجاع",
  Exchange: "شحنة استبدال",
  CashCollection: "تحصيل نقدي",
};

const DELIVERY_TYPE_NUMBER_MAP = {
  10: "Forward",
  15: "CashCollection",
  25: "Return",
  30: "Exchange",
};

export function formatDeliveryType(type, lang) {
  if (type === null || type === undefined) return "—";
  const effectiveLang = lang || getCurrentLanguage();
  const dict = effectiveLang === "ar" ? DELIVERY_TYPE_LABELS_AR : DELIVERY_TYPE_LABELS_EN;

  let key = type;
  if (typeof type === "number" && DELIVERY_TYPE_NUMBER_MAP[type]) {
    key = DELIVERY_TYPE_NUMBER_MAP[type];
  }
  return dict[key] || String(key);
}

/**
 * Normalizes any Egyptian mobile number input (e.g. "01022334455", "1022334455", "+201022334455", "201022334455")
 * into the canonical format: "201022334455" (12 digits starting with 20).
 */
export function normalizeEgyptianPhone(raw) {
  if (!raw) return "";
  let cleaned = String(raw).replace(/\D/g, ""); // digits only

  // If already starts with 20 and has 12 digits (e.g. 201022334455)
  if (cleaned.startsWith("20") && cleaned.length === 12) {
    return cleaned;
  }

  // If 11 digits starting with 01 (e.g. 01022334455 -> 201022334455)
  if (cleaned.startsWith("01") && cleaned.length === 11) {
    return "20" + cleaned.slice(1);
  }

  // If 10 digits starting with 1 (e.g. 1022334455 -> 201022334455)
  if (cleaned.startsWith("1") && cleaned.length === 10) {
    return "20" + cleaned;
  }

  // Fallbacks:
  if (cleaned.startsWith("0")) {
    return "20" + cleaned.slice(1);
  }
  if (!cleaned.startsWith("20")) {
    return "20" + cleaned;
  }
  return cleaned;
}

/**
 * Validates whether the given raw phone number represents a valid Egyptian mobile number.
 * Supported networks: 010 (Vodafone), 011 (Etisalat), 012 (Orange), 015 (WE).
 */
export function validateEgyptianPhone(raw) {
  if (!raw) return false;
  const normalized = normalizeEgyptianPhone(raw);
  return /^201[0125]\d{8}$/.test(normalized);
}

/**
 * Formats a normalized Egyptian phone number for display (e.g. "+20 10 2233 4455" or local "010 2233 4455")
 */
export function formatPhoneForDisplay(phone, useInternational = true) {
  if (!phone) return "";
  const normalized = normalizeEgyptianPhone(phone);
  if (/^201[0125]\d{8}$/.test(normalized)) {
    const country = normalized.slice(0, 2);
    const op = normalized.slice(2, 4);
    const p1 = normalized.slice(4, 8);
    const p2 = normalized.slice(8);
    return useInternational ? `+${country} ${op} ${p1} ${p2}` : `0${op} ${p1} ${p2}`;
  }
  return phone;
}

